import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { sendEmailVerification } from "firebase/auth";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import DashboardNav from "../components/DashboardNav";
import {
  Building2,
  Phone,
  MapPin,
  FileText,
  Mail,
  ShieldCheck,
  AlertCircle,
  Loader2,
  CheckCircle2,
  ArrowLeft,
  Save,
} from "lucide-react";

function PublisherProfilePage() {
  const { currentUser, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [taxId, setTaxId] = useState("");
  const [taxOffice, setTaxOffice] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);

  useEffect(() => {
    if (userProfile?.publisherProfile) {
      const p = userProfile.publisherProfile;
      setCompanyName(p.companyName || "");
      setPhoneNumber(p.phoneNumber || "");
      setAddress(p.address || "");
      setCity(p.city || "");
      setCountry(p.country || "");
      setPostalCode(p.postalCode || "");
      setTaxId(p.taxId || "");
      setTaxOffice(p.taxOffice || "");
    }
  }, [userProfile]);

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!companyName.trim()) {
      setError("Please enter your company name.");
      return;
    }
    if (!phoneNumber.trim()) {
      setError("Please enter your phone number.");
      return;
    }

    setLoading(true);
    try {
      const profileData = {
        companyName: companyName.trim(),
        phoneNumber: phoneNumber.trim(),
        address: address.trim(),
        city: city.trim(),
        country: country.trim(),
        postalCode: postalCode.trim(),
        taxId: taxId.trim(),
        taxOffice: taxOffice.trim(),
        profileCompleted: true,
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(doc(db, "users", currentUser.uid), {
        publisherProfile: profileData,
      });

      if (refreshProfile) await refreshProfile();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to save profile:", err);
      setError("Failed to save profile. Please try again.");
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-1">Publisher Profile</h1>
          <p className="text-gray-400">
            Manage your company information and billing details.
          </p>
        </div>

        <form onSubmit={handleSave} className="space-y-8">
          {/* Email Verification */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <Mail className="w-4 h-4 text-gray-500" />
              Email Verification
            </label>
            {currentUser?.emailVerified ? (
              <div className="flex items-center gap-2 bg-green-500/5 border border-green-500/20 rounded-xl p-4">
                <ShieldCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-400">Email verified</p>
                  <p className="text-xs text-gray-500 mt-0.5">{currentUser.email}</p>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4">
                <div className="flex items-start gap-2.5">
                  <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-300">Email not verified</p>
                    <p className="text-xs text-gray-500 mt-0.5 mb-3">
                      Please verify your email address ({currentUser?.email}) to create campaigns.
                    </p>
                    <button
                      type="button"
                      disabled={sendingVerification || verificationSent}
                      onClick={async () => {
                        setSendingVerification(true);
                        try {
                          await sendEmailVerification(currentUser);
                          setVerificationSent(true);
                        } catch (err) {
                          console.error("Failed to send verification email:", err);
                          setError("Failed to send verification email. Please try again later.");
                        }
                        setSendingVerification(false);
                      }}
                      className="flex items-center gap-2 bg-yellow-500/20 hover:bg-yellow-500/30 disabled:opacity-50 text-yellow-300 px-4 py-2 rounded-lg transition-all text-xs font-medium"
                    >
                      {sendingVerification ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : verificationSent ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <Mail className="w-3.5 h-3.5" />
                      )}
                      {verificationSent ? "Verification email sent!" : "Send Verification Email"}
                    </button>
                    {verificationSent && (
                      <p className="text-xs text-gray-500 mt-2">
                        Didn't receive it? Check your spam folder, then refresh this page after verifying.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Company Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <Building2 className="w-4 h-4 text-gray-500" />
              Company Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Your company or studio name"
              className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <Phone className="w-4 h-4 text-gray-500" />
              Phone Number <span className="text-red-400">*</span>
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+1 (555) 000-0000"
              className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
            />
          </div>

          {/* Address */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              Address
            </label>
            <div className="space-y-3">
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Street address"
                className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
                />
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="Postal code"
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
                />
              </div>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Country"
                className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
            </div>
          </div>

          {/* Tax Information */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FileText className="w-4 h-4 text-gray-500" />
              Tax Information
            </label>
            <p className="text-xs text-gray-600 mb-3">
              Required for invoicing and compliance purposes.
            </p>
            <div className="space-y-3">
              <input
                type="text"
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
                placeholder="Tax ID / VAT Number"
                className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
              <input
                type="text"
                value={taxOffice}
                onChange={(e) => setTaxOffice(e.target.value)}
                placeholder="Tax Office (optional)"
                className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
            </div>
          </div>

          {/* Error / Success */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-xl p-4">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-sm text-green-400 bg-green-500/5 border border-green-500/20 rounded-xl p-4">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              Profile saved successfully!
            </div>
          )}

          {/* Save Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3.5 rounded-xl transition-all font-semibold text-sm"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Profile
          </button>
        </form>
      </div>
    </div>
  );
}

export default PublisherProfilePage;
